import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, SectionCard, StatusBadge, Empty } from "@/components/shared";
import { inr, mt, fmtDate } from "@/lib/format";
import { CheckCircle2, XCircle, ShieldCheck, FolderCheck } from "lucide-react";
import { toast } from "sonner";

export default function Verification() {
  const nav = useNavigate();
  const [data, setData] = useState({ partners: [], recommendations: [], counts: {} });
  const [tab, setTab] = useState("partners");
  const [reasons, setReasons] = useState({});

  const load = () => api.get("/verification/queue").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const setReason = (id, v) => setReasons((s) => ({ ...s, [id]: v }));

  const actPartner = async (p, action) => {
    const reason = reasons[p.id] || (action === "approve" ? "Verified OK" : "");
    if (action === "reject" && !reason) { toast.error("Reason mandatory"); return; }
    try { await api.post(`/partners/${p.id}/verify`, { action, reason }); toast.success(`Partner ${action}d`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const actRec = async (r, action) => {
    const reason = reasons[r.id] || (action === "approve" ? "Verified OK" : "");
    if (!reason) { toast.error("Decision reason mandatory"); return; }
    try { await api.post(`/verification/recommendation/${r.id}/action`, { action, reason }); toast.success(`Recommendation ${action}d`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Verification Desk" subtitle="Inside Sales maker-checker queues. Every decision requires a reason." testid="verification-desk" />

      <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
        <button onClick={() => setTab("partners")} className={`card-surface p-4 text-left ${tab === "partners" ? "ring-2 ring-[var(--copper)]" : ""}`} data-testid="queue-partners">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase"><ShieldCheck size={14} /> Partner KYC</div>
          <p className="text-2xl font-extrabold font-mono mt-1">{data.counts.partners || 0}</p>
        </button>
        <button onClick={() => setTab("recommendations")} className={`card-surface p-4 text-left ${tab === "recommendations" ? "ring-2 ring-[var(--copper)]" : ""}`} data-testid="queue-recommendations">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase"><FolderCheck size={14} /> Project Recos</div>
          <p className="text-2xl font-extrabold font-mono mt-1">{data.counts.recommendations || 0}</p>
        </button>
      </div>

      {tab === "partners" && (
        <SectionCard className="!p-0" testid="partner-verify-queue">
          <div className="divide-y divide-slate-50">
            {data.partners.length === 0 && <Empty text="Queue clear — no pending partners." />}
            {data.partners.map((p) => (
              <div key={p.id} className="p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px] cursor-pointer" onClick={() => nav(`/partners/${p.id}`)}>
                  <p className="font-medium text-slate-800">{p.name} <span className="font-mono text-xs text-[var(--copper)]">{p.partner_id}</span></p>
                  <p className="text-xs text-slate-400">{p.partner_type} · {p.city} · {p.mobile} · {p.company}</p>
                </div>
                <input value={reasons[p.id] || ""} onChange={(e) => setReason(p.id, e.target.value)} placeholder="Reason…" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-44" data-testid={`reason-${p.partner_id}`} />
                <button data-testid={`approve-${p.partner_id}`} onClick={() => actPartner(p, "approve")} className="flex items-center gap-1 bg-emerald-600 text-white text-sm px-3 py-1.5 rounded-lg font-semibold"><CheckCircle2 size={14} /> Approve</button>
                <button data-testid={`reject-${p.partner_id}`} onClick={() => actPartner(p, "reject")} className="flex items-center gap-1 bg-red-600 text-white text-sm px-3 py-1.5 rounded-lg font-semibold"><XCircle size={14} /> Reject</button>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {tab === "recommendations" && (
        <SectionCard className="!p-0" testid="rec-verify-queue">
          <div className="divide-y divide-slate-50">
            {data.recommendations.length === 0 && <Empty text="No pending recommendations." />}
            {data.recommendations.map((r) => (
              <div key={r.id} className="p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px] cursor-pointer" onClick={() => nav(`/projects/${r.master_id}`)}>
                  <p className="font-medium text-slate-800">{r.partner_name} <span className="font-mono text-xs text-[var(--copper)]">{r.project_id}</span></p>
                  <p className="text-xs text-slate-400">{r.partner_type} · {mt(r.tonnage_mt)} · {inr(r.est_value_inr)} · {r.payout_eligible ? "Payout eligible" : "Analytics only"} · {r.confidence}% match</p>
                </div>
                <input value={reasons[r.id] || ""} onChange={(e) => setReason(r.id, e.target.value)} placeholder="Reason…" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-44" />
                <button onClick={() => actRec(r, "approve")} className="flex items-center gap-1 bg-emerald-600 text-white text-sm px-3 py-1.5 rounded-lg font-semibold"><CheckCircle2 size={14} /> Verify</button>
                <button onClick={() => actRec(r, "reject")} className="flex items-center gap-1 bg-red-600 text-white text-sm px-3 py-1.5 rounded-lg font-semibold"><XCircle size={14} /> Reject</button>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}
